
import React, { useState } from 'react';
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Check, X } from "lucide-react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TerpeneWithPercentage } from "@/types";
import { TerpeneProps } from "@/types/componentProps";

const TERPENE_OPTIONS = [
  'Myrcene', 'Limonene', 'Caryophyllene', 'Terpinolene',
  'Pinene', 'Humulene', 'Linalool', 'Ocimene',
  'Terpineol', 'Valencene', 'Geraniol', 'Bisabolol'
];

const TerpenePercentages = ({ product, onChange }: TerpeneProps) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const terpeneInfo = product.terpene_info || { major_terpenes: [], total_terpenes_percentage: 0 };
  const { major_terpenes, total_terpenes_percentage } = terpeneInfo;

  const handleTerpeneSelect = (selectedTerpene: string) => {
    if (major_terpenes.some(t => t.name === selectedTerpene)) {
      // If already selected, remove it
      const updatedTerpenes = major_terpenes.filter(t => t.name !== selectedTerpene);
      onChange({
        terpene_info: {
          ...terpeneInfo,
          major_terpenes: updatedTerpenes
        }
      });
    } else if (major_terpenes.length < 3) {
      // If not selected and we have room, add it
      const updatedTerpenes = [
        ...major_terpenes,
        { name: selectedTerpene, percentage: 0 }
      ];
      onChange({
        terpene_info: {
          ...terpeneInfo,
          major_terpenes: updatedTerpenes
        }
      });
    }
  };

  const handleRemoveTerpene = (terpeneToRemove: string) => {
    const updatedTerpenes = major_terpenes.filter(t => t.name !== terpeneToRemove);
    onChange({
      terpene_info: {
        ...terpeneInfo,
        major_terpenes: updatedTerpenes
      }
    });
  };

  const handleUpdateTotalPercentage = (value: string) => {
    const percentage = parseFloat(value);
    if (isNaN(percentage)) return;

    onChange({
      terpene_info: {
        ...terpeneInfo,
        total_terpenes_percentage: percentage
      }
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Total Terpenes</Label>
        <Input
          type="text"
          inputMode="decimal"
          value={total_terpenes_percentage || ''}
          onChange={(e) => handleUpdateTotalPercentage(e.target.value)}
          placeholder="0"
        />
      </div>

      <div className="space-y-2">
        <Label>Select Major Terpenes (Max 3)</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="flex flex-wrap gap-2 p-2 border rounded min-h-[42px] cursor-pointer">
              {major_terpenes.length > 0 ? (
                major_terpenes.map((terpene) => (
                  <div
                    key={terpene.name}
                    className="flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded"
                  >
                    {terpene.name}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveTerpene(terpene.name);
                      }}
                      className="text-secondary-foreground hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-muted-foreground">Select terpenes...</div>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0">
            <Command>
              <CommandInput 
                placeholder="Search terpenes..." 
                value={searchValue}
                onValueChange={setSearchValue}
              />
              <CommandList>
                <CommandEmpty>No terpenes found.</CommandEmpty>
                <CommandGroup>
                  {TERPENE_OPTIONS
                    .filter(terpene => 
                      terpene.toLowerCase().includes(searchValue.toLowerCase())
                    )
                    .map((terpene) => (
                      <CommandItem
                        key={terpene}
                        value={terpene}
                        onSelect={() => handleTerpeneSelect(terpene)}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-4">
                            {major_terpenes.some(t => t.name === terpene) && (
                              <Check className="h-4 w-4" />
                            )}
                          </div>
                          {terpene}
                        </div>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default TerpenePercentages;
